import { useEffect, useState } from "react";
import api from "../lib/api";
import { ShieldCheck, Globe, LockKey, Info } from "@phosphor-icons/react";

const SCOPE_LABEL = { full: "Full", public: "Public-information only", off: "Off" };

export const AiTransparency = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/ai-transparency").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data) return null;

  const groups = [];
  data.rows.forEach((r) => {
    const g = groups.find((x) => x.module === r.module);
    if (g) g.rows.push(r); else groups.push({ module: r.module, rows: [r] });
  });

  return (
    <div className="border border-border bg-card p-5" data-testid="ai-transparency-panel">
      <div className="flex items-start gap-3 mb-1">
        <ShieldCheck size={20} className="mt-0.5 text-primary shrink-0" />
        <div>
          <h3 className="font-semibold">AI Data Transparency</h3>
          <p className="text-xs text-muted-foreground">
            Every AI-powered function, exactly what it transmits, and which AI scope permits it.
            Current entity scope:{" "}
            <span className="font-semibold" data-testid="ai-transparency-scope">
              {SCOPE_LABEL[data.current_scope] || data.current_scope}
            </span>.
          </p>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-xs" data-testid="ai-transparency-table">
          <thead className="bg-secondary">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Module / Feature</th>
              <th className="text-left px-3 py-2 font-semibold">What is sent to the AI</th>
              <th className="text-left px-3 py-2 font-semibold">Data class</th>
              <th className="text-left px-3 py-2 font-semibold">Runs when scope is</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => g.rows.map((r, i) => (
              <tr key={`${g.module}-${i}`} className="border-t border-border align-top">
                <td className="px-3 py-2">
                  {i === 0 && <div className="font-semibold text-[11px] uppercase tracking-wider text-muted-foreground">{g.module}</div>}
                  <div>{r.feature}</div>
                </td>
                <td className="px-3 py-2 text-muted-foreground max-w-[26rem]">{r.sends}</td>
                <td className="px-3 py-2">
                  {r.data_class === "public" ? (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5">
                      <Globe size={11} /> Public info
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-800 bg-amber-50 border border-amber-300 px-1.5 py-0.5">
                      <LockKey size={11} weight="fill" /> Confidential
                    </span>
                  )}
                  {r.confirm_required && (
                    <div className="text-[10px] text-muted-foreground mt-1">User confirmation required</div>
                  )}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {r.allowed_scopes.includes("public") ? "Full · Public-only" : "Full only"}
                </td>
              </tr>
            )))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 border border-border bg-secondary/40 p-3" data-testid="ai-transparency-no-ai">
        <p className="text-xs font-semibold mb-1.5">Processed entirely in-app — never sent to any AI provider:</p>
        <ul className="text-xs text-muted-foreground grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-0.5 list-disc pl-4">
          {data.no_ai_functions.map((f) => <li key={f}>{f}</li>)}
        </ul>
      </div>

      <p className="mt-3 text-xs text-muted-foreground">{data.provider_note}</p>

      <div className="mt-3 flex items-start gap-2 border-t border-border pt-3" data-testid="ai-transparency-footnote">
        <Info size={14} className="mt-0.5 shrink-0 text-primary" />
        <p className="text-xs italic text-muted-foreground">{data.footnote}</p>
      </div>
    </div>
  );
};

export default AiTransparency;
